/**
 * @krisspy-file
 * @type page
 * @name "ForgotPasswordPage"
 * @title "Zapomenuté heslo"
 * @description "Stránka pro reset hesla – uživatel zadá e-mail a obdrží odkaz na reset"
 * @routes ["/forgot-password"]
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle, CheckCircle, Map, ArrowLeft } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!isSupabaseConfigured) {
      setLoading(false);
      setError('Reset hesla není v demo režimu dostupný.');
      return;
    }

    try {
      const { error: fnError } = await supabase.functions.invoke('send-reset-email', {
        body: {
          email: email.trim().toLowerCase(),
          redirectTo: `${window.location.origin}/reset-password`,
        },
      });

      setLoading(false);

      // Edge funkce vrací success vždy (bezpečnostní princip - nezveřejňovat
      // jestli email v DB existuje). Real chyba znamená výpadek serveru
      // nebo špatně nakonfigurovaný Resend.
      if (fnError) {
        setError('Email se nepodařilo odeslat. Zkuste to prosím za chvíli.');
        console.error('send-reset-email error:', fnError);
      } else {
        setSent(true);
      }
    } catch (err: any) {
      setLoading(false);
      setError('Email se nepodařilo odeslat. Zkuste to prosím za chvíli.');
      console.error('send-reset-email exception:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl shadow-lg mb-4">
            <Map className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900 mb-1">Journey Mapper</h1>
          <p className="text-gray-500 text-sm">Mapujte cestu. Objevujte příležitosti.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Zapomenuté heslo</h2>
          <p className="text-sm text-gray-500 mb-6">
            Zadejte svůj e-mail a pokud k němu existuje účet, pošleme vám odkaz pro reset hesla.
          </p>

          {error && (
            <div className="mb-5 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {sent ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-emerald-600" />
              </div>
              <p className="text-sm text-gray-700 text-center leading-relaxed">
                Pokud k e-mailu <strong>{email}</strong> existuje účet, poslali jsme na něj odkaz pro reset hesla.
              </p>
              <p className="text-xs text-gray-400 text-center">
                Zkontrolujte i složku Spam.
              </p>
              <Link
                to="/login"
                className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Zpět na přihlášení
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  E-mail
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all placeholder-gray-400"
                    placeholder="vas@email.cz"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Odesílání...
                  </>
                ) : (
                  'Odeslat odkaz pro reset'
                )}
              </button>

              <Link to="/login" className="flex items-center justify-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors mt-2">
                <ArrowLeft className="w-4 h-4" /> Zpět na přihlášení
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
